// src/redux/reducer/ozonUnit/action/action.js
import axios from "axios";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import sockets from "../../../../socket/socket";

dayjs.extend(customParseFormat);

// В Redux и на сервер отправляем безопасный формат.
// В DatePicker можно показывать YYYY.MM.DD через format в компоненте.
const apiDateFormat = "YYYY-MM-DD";

// ─────────── Types ───────────
export const FETCH_OZON_BY_SKU_REQUEST = "FETCH_OZON_BY_SKU_REQUEST";
export const FETCH_OZON_BY_SKU_SUCCESS = "FETCH_OZON_BY_SKU_SUCCESS";
export const FETCH_OZON_BY_SKU_FAILURE = "FETCH_OZON_BY_SKU_FAILURE";

export const SET_DATE_RANGE_OZON_BY_SKU = "SET_DATE_RANGE_OZON_BY_SKU";
export const SORT_OZON_BY_SKU = "SORT_OZON_BY_SKU";
export const SET_EMPTY_ARRAY_OZON_BY_SKU = "SET_EMPTY_ARRAY_OZON_BY_SKU";
export const RESET_OZON_BY_SKU = "RESET_OZON_BY_SKU";

// adPlanPercent
export const UPDATE_ADPLAN_PERCENT_SKU = "UPDATE_ADPLAN_PERCENT_SKU";
export const UPDATE_ADPLAN_PERCENT_ALL = "UPDATE_ADPLAN_PERCENT_ALL";

// taxPercent
export const UPDATE_TAX_PERCENT_SKU = "UPDATE_TAX_PERCENT_SKU";
export const UPDATE_TAX_PERCENT_ALL = "UPDATE_TAX_PERCENT_ALL";

// cost
export const UPDATE_COST_PRICE_SKU = "UPDATE_COST_PRICE_SKU";
export const UPDATE_COST_PRICE_ALL = "UPDATE_COST_PRICE_ALL";

export const PATCH_COSTS_OZON_BY_SKU = "PATCH_COSTS_OZON_BY_SKU";

// ─────────── Date helpers ───────────
function safeDayjs(value) {
  if (!value) return null;

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }

  // Date object
  if (value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }

  const s = String(value).trim();

  if (
    !s ||
    s === "Invalid Date" ||
    s === "undefined" ||
    s === "null"
  ) {
    return null;
  }

  // Самые важные форматы. НЕ используем dayjs(s) для строк с точками.
  const byDash = dayjs(s, "YYYY-MM-DD", true);
  if (byDash.isValid()) return byDash;

  const byDot = dayjs(s, "YYYY.MM.DD", true);
  if (byDot.isValid()) return byDot;

  // На всякий случай, если прилетел ISO.
  const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (isoMatch) {
    const d = dayjs(isoMatch[1], "YYYY-MM-DD", true);
    return d.isValid() ? d : null;
  }

  return null;
}

function getDefaultRange() {
  const to = dayjs();
  const from = dayjs().subtract(7, "day");

  return {
    date_from: from.format(apiDateFormat),
    date_to: to.format(apiDateFormat),
  };
}

function normalizeDateForApi(value) {
  const d = safeDayjs(value);
  return d ? d.format(apiDateFormat) : "";
}

function normalizeRange(value) {
  const [rawFrom, rawTo] = Array.isArray(value)
    ? value
    : [value?.from ?? value?.date_from, value?.to ?? value?.date_to];

  const date_from = normalizeDateForApi(rawFrom);
  const date_to = normalizeDateForApi(rawTo);

  if (!date_from || !date_to) {
    return getDefaultRange();
  }

  return { date_from, date_to };
}

// ─────────── Thunk ───────────
// getOzonBySkuAction({ url, date_from, date_to, q, page, limit, sortField, sortDir })
export const getOzonBySkuAction =
  ({
    url = "/ozon-transactions-by-sku",
    date_from,
    date_to,
    q,
    page = 1,
    limit = 20,
    sortField = "revenueTotal",
    sortDir = "desc",
  } = {}) =>
  async (dispatch, getState) => {
    try {
      dispatch({ type: FETCH_OZON_BY_SKU_REQUEST });

      const state = getState();
      const slice = state.ozonUnit ?? state.ozonBySku ?? {};

      let from = normalizeDateForApi(date_from ?? slice.date_from);
      let to = normalizeDateForApi(date_to ?? slice.date_to);

      // ВАЖНО: никогда не отправляем Invalid Date на сервер.
      if (!from || !to) {
        const def = getDefaultRange();
        from = def.date_from;
        to = def.date_to;

        // Чистим битые даты в Redux, если они там уже были.
        dispatch({
          type: SET_DATE_RANGE_OZON_BY_SKU,
          payload: def,
        });
      }

      const resp = await axios.get(url, {
        params: {
          date_from: from,
          date_to: to,
          q,
          page,
          limit,
          sortField,
          sortDir,
        },
      });

      dispatch({
        type: FETCH_OZON_BY_SKU_SUCCESS,
        payload: {
          data: resp?.data?.data ?? [],
          totalElements: resp?.data?.totalElements ?? 0,
          totalPages: resp?.data?.totalPages ?? 1,
          currentPage: resp?.data?.currentPage ?? Number(page || 1),
          pageSize: resp?.data?.pageSize ?? Number(limit || 20),
          totals: resp?.data?.totals ?? {},
          sort: resp?.data?.sort ?? { field: sortField, dir: sortDir },
        },
      });
    } catch (error) {
      dispatch({
        type: FETCH_OZON_BY_SKU_FAILURE,
        payload: error?.message || "Ошибка загрузки",
      });
      throw error;
    }
  };

// ─────────── Simple actions ───────────
export const setDateRangeOzon = (value) => {
  // value может быть массивом [from, to] из RangePicker
  // или объектом { from, to } / { date_from, date_to }.
  // Тут больше НЕЛЬЗЯ делать dayjs(from).format(...),
  // потому что dayjs("Invalid Date").format(...) снова даёт "Invalid Date".
  return {
    type: SET_DATE_RANGE_OZON_BY_SKU,
    payload: normalizeRange(value),
  };
};

export const sortOzonBySku = (field) => ({
  type: SORT_OZON_BY_SKU,
  payload: { field },
});

export const clearOzonData = () => ({ type: SET_EMPTY_ARRAY_OZON_BY_SKU });

export const resetOzonState = () => ({ type: RESET_OZON_BY_SKU });

export const updateAdPlanPercentSku = ({ sku, percent }) => ({
  type: UPDATE_ADPLAN_PERCENT_SKU,
  payload: { sku, adPlanPercent: Number(percent), adPlanEdited: true },
});

export const updateAdPlanPercentAll = ({ percent }) => ({
  type: UPDATE_ADPLAN_PERCENT_ALL,
  payload: { adPlanPercent: Number(percent), adPlanEdited: true },
});

export const updateTaxPercentSku = ({ sku, percent }) => ({
  type: UPDATE_TAX_PERCENT_SKU,
  payload: { sku, taxPercent: Number(percent), taxEdited: true },
});

export const updateTaxPercentAll = ({ percent }) => ({
  type: UPDATE_TAX_PERCENT_ALL,
  payload: { taxPercent: Number(percent), taxEdited: true },
});

export const updateCostPriceSku = ({ sku, amount }) => ({
  type: UPDATE_COST_PRICE_SKU,
  payload: {
    sku,
    costPrice: Number(amount),
    amount: Number(amount),
    mode: "amount",
    costPriceEdited: true,
  },
});

export const updateCostPriceAll = ({ amount }) => ({
  type: UPDATE_COST_PRICE_ALL,
  payload: {
    costPrice: Number(amount),
    amount: Number(amount),
    mode: "amount",
    costPriceEdited: true,
  },
});

export const patchOzonCosts = (rows) => ({
  type: PATCH_COSTS_OZON_BY_SKU,
  payload: rows, // [{ sku, amount }]
});

// ── apply all ───────────────────────────────────────────────────────────────
export const saveAllCostPrice = ({ amount }) => async (dispatch, getState) => {
  const num = Number(String(amount).replace(/\s/g, "").replace(",", "."));
  const { data } = await axios.post("/sku-cost-price/apply-all", { amount: num });

  let rows = data?.updatedSkus;
  if (!Array.isArray(rows) || rows.length === 0) {
    const state = getState();
    const stateRows = (state.ozonUnit ?? state.ozonBySku ?? {}).data ?? [];
    rows = stateRows.map((r) => ({
      sku: r.sku,
      amount: num,
      mode: "amount",
      costPriceEdited: true,
    }));
  }

  dispatch(patchOzonCosts(rows));
  sockets.emit("updateUnitOzon", { applyAll: true, amount: num });

  return rows;
};

// ── single SKU ─────────────────────────────────────────────────────────────
export const saveSkuCostPrice = ({ sku, amount }) => async (dispatch) => {
  const num = Number(String(amount).replace(/\s/g, "").replace(",", "."));
  const { data } = await axios.post("/sku-cost-price/api", { sku, amount: num });

  const rows = data?.updatedSkus ?? [
    { sku, amount: num, mode: "amount", costPriceEdited: true },
  ];

  dispatch(patchOzonCosts(rows));
  sockets.emit("updateUnitOzon", { sku, applyAll: false, amount: num });

  return rows;
};
